import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { getProducts, getCategories } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useAuth } from '../contexts/AuthContext';
import LoginSignupDialog from '../components/LoginSignupDialog';

const ProductCard = ({ product }) => {
  const isUnavailable = product.available === false;

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        width: '95%',
        maxWidth: '95%',
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isUnavailable ? 'rgba(0,0,0,0.08)' : 'var(--color-sand)',
        overflow: 'hidden',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        opacity: isUnavailable ? 0.82 : 1,
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 16px 48px rgba(44,41,38,0.12)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          paddingTop: '100%',
          bgcolor: 'var(--color-sand)',
          overflow: 'hidden',
        }}
      >
        {product.imageUrl ? (
          <CardMedia
            component="img"
            image={product.imageUrl}
            alt={product.name}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-warm-gray)',
              opacity: 0.5,
            }}
          >
            <RestaurantIcon sx={{ fontSize: 48 }} />
          </Box>
        )}
        {isUnavailable && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 1,
            }}
          >
            <Chip
              icon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Unavailable"
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: 1.5,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                '& .MuiChip-icon': { color: 'rgba(255,255,255,0.9)' },
              }}
            />
          </Box>
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 1.9, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontWeight: 600,
            fontSize: '1.09rem',
            color: 'var(--color-charcoal)',
            lineHeight: 1.3,
            mb: 0.5,
          }}
        >
          {product.name}
        </Typography>
        {product.description && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--color-warm-gray)',
              fontSize: '0.81rem',
              lineHeight: 1.5,
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.description}
          </Typography>
        )}
        <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--color-accent)' }}>
            {product.price != null ? formatCurrency(product.price) : '—'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const MenuPage = () => {
  const { isLoggedIn } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [categoriesFromApi, setCategoriesFromApi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    if (!isLoggedIn) {
      setProducts([]);
      setCategoriesFromApi([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([getProducts(), getCategories()])
      .then(([productsRes, categoriesRes]) => {
        const data = productsRes.data;
        const list = Array.isArray(data) ? data : data?.data ?? [];
        setProducts(Array.isArray(list) ? list : []);
        const catData = categoriesRes.data;
        const catList = Array.isArray(catData) ? catData : catData?.data ?? [];
        setCategoriesFromApi(Array.isArray(catList) ? catList : []);
      })
      .catch(() => {
        setProducts([]);
        setCategoriesFromApi([]);
      })
      .finally(() => setLoading(false));
  }, [isLoggedIn]);

  const categoryNameMap = React.useMemo(() => {
    const map = {};
    categoriesFromApi.forEach((c) => {
      if (c && c._id != null) map[c._id] = c.name || c._id;
    });
    return map;
  }, [categoriesFromApi]);

  const categories = React.useMemo(() => {
    const set = new Set(products.map((p) => (p.category != null && p.category !== '' ? p.category : null)).filter(Boolean));
    return ['all', ...Array.from(set).sort()];
  }, [products]);

  const getCategoryLabel = (categoryId) => {
    if (categoryId === 'all') return 'All';
    return categoryNameMap[categoryId] ?? categoryId ?? 'Other';
  };

  const filteredProducts =
    categoryFilter === 'all'
      ? products
      : products.filter((p) => (p.category != null ? p.category : null) === categoryFilter);

  const productsByCategory = React.useMemo(() => {
    if (categoryFilter !== 'all') return [];
    const byId = {};
    products.forEach((p) => {
      const cid = p.category != null && p.category !== '' ? p.category : '__other';
      if (!byId[cid]) byId[cid] = [];
      byId[cid].push(p);
    });
    const order = [...categories].filter((c) => c !== 'all');
    if (byId['__other']?.length) order.push('__other');
    return order.map((cid) => ({
      categoryId: cid,
      categoryName: cid === '__other' ? 'Other' : (categoryNameMap[cid] ?? cid),
      products: byId[cid] || [],
    })).filter((s) => s.products.length > 0);
  }, [categoryFilter, products, categories, categoryNameMap]);

  return (
    <Box sx={{ pt: 10, pb: 8, minHeight: '100vh', bgcolor: 'var(--color-cream)' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
        >
          Food &amp; Drinks
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: { xs: '2.25rem', md: '3rem' },
            fontWeight: 500,
            mt: 1,
            mb: 1,
            color: 'var(--color-charcoal)',
          }}
        >
          Our Menu
        </Typography>
        <Typography sx={{ color: 'var(--color-warm-gray)', mb: 4, maxWidth: 560 }}>
          Fresh, local ingredients. Order at the inn or ask about availability.
        </Typography>

        {!isLoggedIn ? (
          <Box sx={{ maxWidth: 500, mx: 'auto', textAlign: 'center', py: 8 }}>
            <Typography variant="h5" sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500, mb: 2, color: 'var(--color-charcoal)' }}>
              Login to view our menu
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Sign in or create an account to browse food and drinks.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => setLoginOpen(true)}
              sx={{ bgcolor: 'var(--color-accent)', '&:hover': { bgcolor: 'var(--color-charcoal)' } }}
            >
              Login
            </Button>
            <LoginSignupDialog open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => setLoginOpen(false)} />
          </Box>
        ) : (
        <>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: 'var(--color-accent)' }} />
          </Box>
        ) : products.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <RestaurantIcon sx={{ fontSize: 64, color: 'var(--color-warm-gray)', opacity: 0.4, mb: 2 }} />
            <Typography color="text.secondary">No menu items at the moment. Check back soon.</Typography>
            <Button component={Link} to="/" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
              Back to Home
            </Button>
          </Box>
        ) : (
          <>
            {categories.length > 2 && (
              <ToggleButtonGroup
                value={categoryFilter}
                exclusive
                onChange={(_, v) => v != null && setCategoryFilter(v)}
                sx={{
                  mb: 3,
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiToggleButtonGroup-grouped': { borderRadius: '8px !important' },
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: 2,
                    border: '1px solid var(--color-sand)',
                    '&.Mui-selected': {
                      bgcolor: 'var(--color-accent)',
                      color: 'white',
                      borderColor: 'var(--color-accent)',
                      '&:hover': { bgcolor: 'var(--color-accent-light)' },
                    },
                  },
                }}
              >
                {categories.map((cat) => (
                  <ToggleButton key={cat} value={cat}>
                    {getCategoryLabel(cat)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {categoryFilter === 'all' && productsByCategory.length > 0 ? (
              productsByCategory.map((section) => (
                <Box key={section.categoryId} sx={{ mb: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                      fontWeight: 600,
                      color: 'var(--color-charcoal)',
                      mb: 2,
                      pb: 1,
                      borderBottom: '2px solid var(--color-sand)',
                    }}
                  >
                    {section.categoryName}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                      gap: { xs: 2.85, sm: 2.85, md: 3, lg: 3 },
                      minHeight: { xs: 200, sm: 200 },
                    }}
                  >
                    {section.products.map((product) => (
                      <ProductCard key={product._id} product={product} />
                    ))}
                  </Box>
                </Box>
              ))
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                  gap: { xs: 2.85, sm: 2.85, md: 3, lg: 3 },
                  minHeight: { xs: 200, sm: 200 },
                }}
              >
                {filteredProducts.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </Box>
            )}

            <Box sx={{ mt: 5, textAlign: 'center' }}>
              <Button
                component={Link}
                to="/"
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: 'var(--color-sand)',
                  color: 'var(--color-charcoal)',
                  '&:hover': { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' },
                }}
              >
                Back to Home
              </Button>
            </Box>
          </>
        )}
        </>
        )}
      </Box>
    </Box>
  );
};

export default MenuPage;
